/**
 * Cooling system edit form body — single "Cooling System" details card (#996 Phase 2).
 * Hand-written for the 8 per-instance cooling fields (NOT a mechanical mirror of the
 * UPS field set). Every field is wrapped in PermissionAwareField for RBAC.
 *
 * Step-level `coolingAdditionalReadinessInfo` renders here via
 * {@link RepeatableInstanceAdditionalInfo} when editing a cooling system (#1154).
 */

import React, { useCallback } from 'react';
import { Platform, StyleSheet } from 'react-native';

import {
  COOLING_SYSTEM_EDIT_DETAILS_CARD_SUBTITLE,
  COOLING_SYSTEM_EDIT_STATUS_EXISTING_LABEL,
  COOLING_SYSTEM_EDIT_TYPE_LABEL,
  COOLING_SYSTEM_EDIT_TYPE_OPTIONS,
} from '../../constants/coolingSystemConfigurationsSubsectionCopy';
import { COOLING_SYSTEM_PLACEHOLDERS } from '../../constants/coolingSystemRequirementsUi';
import { REDUNDANCY_OPTIONS } from '../../constants/redundancy';
import {
  COOLING_NOTES_SUBSECTION_FIELDS,
  COOLING_SYSTEM_EDITOR_SUBSECTION_FIELDS,
} from '../../constants/requestFieldDescriptors';
import { SECTION_IDS, getSectionLabel } from '../../constants/sectionIds';
import { useFormFieldVisibilityContext } from '../../hooks/useFormFieldVisibilityContext';
import { spacing } from '../../theme';
import {
  resolveRepeatableInstanceAttachmentSectionId,
  selectAttachmentsForRepeatableInstance,
  selectSelectedFilesForRepeatableInstance,
} from '../../utils/repeatableInstanceAttachmentSections';
import { subsectionHeaderMetricsProps } from '../../utils/subsectionHeaderMetrics';
import { booleanToYesNo } from '../../utils/yesNoBoolean';
import {
  FormRow,
  FormColumn,
  FormInput,
  FormDropdown,
  FormYesNoChoice,
  FormTwoOptionChoice,
  UnitNumberField,
  formFieldInStack,
  FormStackColumnsOnWebProvider,
  StepForm,
} from '../forms';
import { LocationTagPicker } from '../locationTags/LocationTagPicker';
import { PermissionAwareField } from '../PermissionAwareField';
import { RepeatableInstanceAdditionalInfo } from '../repeatableSections/repeatableInstanceAdditionalInfo';

import type { SavedAttachment } from '../../services/attachmentService';
import type {
  CoolingSystemStatus,
  CoolingSystemConfiguration,
} from '../../types/coolingSystem';
import type { SelectedFile } from '../forms/FileUpload';

export interface CoolingSystemEditFormAttachmentProps {
  attachmentsBySection?: Record<string, SavedAttachment[]>;
  onAttachFiles?: (sectionId: string) => void;
  onDeleteAttachment?: (attachmentId: number) => void;
  selectedFiles?: Record<string, SelectedFile[]>;
  onFilesSelected?: (sectionId: string, files: SelectedFile[]) => void;
  isOffline?: boolean;
  onCaptionChange?: (attachmentId: number, caption: string) => void;
  pendingCaptionEdits?: Record<number, string>;
}

export interface CoolingSystemEditFormContentProps
  extends CoolingSystemEditFormAttachmentProps {
  formData: Partial<CoolingSystemConfiguration>;
  updateField: <K extends keyof CoolingSystemConfiguration>(
    field: K,
    value: CoolingSystemConfiguration[K],
  ) => void;
  disabled?: boolean;
  requestStage?: string;
  requestId?: string;
  testID?: string;
  showAdditionalInformation?: boolean;
  /** Marks the editor dirty when per-instance Additional Information attachments change. */
  onSharedFieldDirty?: () => void;
  showFullDetails?: boolean;
}

function coolingSystemStatusToTypeLabel(
  status: CoolingSystemStatus | undefined,
): string {
  return status === 'existing'
    ? COOLING_SYSTEM_EDIT_TYPE_OPTIONS[0]
    : COOLING_SYSTEM_EDIT_TYPE_OPTIONS[1];
}

function coolingSystemTypeLabelToStatus(label: string): CoolingSystemStatus {
  return label === COOLING_SYSTEM_EDIT_STATUS_EXISTING_LABEL
    ? 'existing'
    : 'new';
}

function CoolingSystemPermissionField({
  fieldId,
  requestStage,
  requestId,
  children,
}: {
  fieldId: string;
  requestStage?: string;
  requestId?: string;
  children: React.ReactNode;
}) {
  if (!requestStage) {
    return <>{children}</>;
  }
  return (
    <PermissionAwareField
      fieldId={fieldId}
      requestStage={requestStage}
      requestId={requestId}
      containerStyle={formFieldInStack}
    >
      {children}
    </PermissionAwareField>
  );
}

const COOLING_SYSTEM_EDITOR_SUBSECTION_PERSIST = {
  persistSectionExpanded: true,
} as const;

export const CoolingSystemEditFormContent: React.FC<
  CoolingSystemEditFormContentProps
> = ({
  formData,
  updateField,
  disabled = false,
  requestStage,
  requestId,
  attachmentsBySection,
  onAttachFiles,
  onDeleteAttachment,
  selectedFiles,
  onFilesSelected,
  isOffline = false,
  onCaptionChange,
  pendingCaptionEdits,
  testID = 'cooling-system-edit-form',
  showAdditionalInformation = false,
  onSharedFieldDirty,
  showFullDetails = true,
}) => {
  const permissionStage = requestStage ?? 'DRAFT';
  const fieldVisibility = useFormFieldVisibilityContext({
    showFullDetails,
    requestStage: permissionStage,
    requestId,
  });
  const coolingSystemMetrics = subsectionHeaderMetricsProps(
    COOLING_SYSTEM_EDITOR_SUBSECTION_FIELDS,
    formData as Record<string, unknown>,
    fieldVisibility,
  );
  const attachmentsEnabled = true;
  const configurationId = formData.id;
  const detailsBaseSectionId = SECTION_IDS.COOLING_READINESS_ADVANCED;
  const detailsSectionId = resolveRepeatableInstanceAttachmentSectionId(
    detailsBaseSectionId,
    configurationId,
  );
  const detailsAttachments = selectAttachmentsForRepeatableInstance(
    attachmentsBySection ?? {},
    detailsBaseSectionId,
    configurationId,
  );
  const detailsSelectedFiles = selectSelectedFilesForRepeatableInstance(
    selectedFiles ?? {},
    detailsBaseSectionId,
    configurationId,
  );
  const additionalSectionId = resolveRepeatableInstanceAttachmentSectionId(
    SECTION_IDS.COOLING_READINESS_ADDITIONAL,
    configurationId,
  );
  const markSharedDirty = useCallback(() => {
    onSharedFieldDirty?.();
  }, [onSharedFieldDirty]);

  return (
    <FormStackColumnsOnWebProvider enabled={Platform.OS === 'web'}>
      <StepForm
        title={getSectionLabel(detailsBaseSectionId)}
        titleSubtitle={COOLING_SYSTEM_EDIT_DETAILS_CARD_SUBTITLE}
        defaultSectionExpanded
        {...COOLING_SYSTEM_EDITOR_SUBSECTION_PERSIST}
        {...coolingSystemMetrics}
        sectionId={detailsSectionId}
        requestId={requestId}
        requestStage={permissionStage}
        allowAttachments={attachmentsEnabled}
        attachments={detailsAttachments}
        onAttachFiles={
          onAttachFiles ? () => onAttachFiles(detailsSectionId) : undefined
        }
        onDeleteAttachment={onDeleteAttachment}
        selectedFiles={detailsSelectedFiles}
        onFilesSelected={
          onFilesSelected
            ? files => onFilesSelected(detailsSectionId, files)
            : undefined
        }
        isOffline={isOffline}
        onCaptionChange={onCaptionChange}
        pendingCaptionEdits={pendingCaptionEdits}
        isEditing={!disabled}
        disabled={disabled}
        containerStyle={styles.sectionCard}
      >
        <FormRow>
          <FormColumn>
            <CoolingSystemPermissionField
              fieldId="name"
              requestStage={requestStage}
              requestId={requestId}
            >
              <FormInput
                label="Name"
                value={formData.name || ''}
                onChangeText={value => updateField('name', value)}
                placeholder="Enter a name for this cooling system (e.g., CRAC A)"
                disabled={disabled}
                testID={`${testID}-name`}
                containerStyle={formFieldInStack}
              />
            </CoolingSystemPermissionField>
          </FormColumn>
          <FormColumn>
            <CoolingSystemPermissionField
              fieldId="status"
              requestStage={requestStage}
              requestId={requestId}
            >
              <FormTwoOptionChoice
                label={COOLING_SYSTEM_EDIT_TYPE_LABEL}
                value={coolingSystemStatusToTypeLabel(formData.status)}
                options={[
                  COOLING_SYSTEM_EDIT_TYPE_OPTIONS[0],
                  COOLING_SYSTEM_EDIT_TYPE_OPTIONS[1],
                ]}
                onSelect={label =>
                  updateField('status', coolingSystemTypeLabelToStatus(label))
                }
                disabled={disabled}
                inline
                testID={`${testID}-type`}
                containerStyle={formFieldInStack}
              />
            </CoolingSystemPermissionField>
          </FormColumn>
        </FormRow>

        <FormRow>
          <FormColumn>
            <CoolingSystemPermissionField
              fieldId="locationTagId"
              requestStage={requestStage}
              requestId={requestId}
            >
              <LocationTagPicker
                requestId={requestId}
                requestStage={requestStage}
                value={formData.locationTagId ?? null}
                onChange={tagId => updateField('locationTagId', tagId)}
                disabled={disabled}
                testID={`${testID}-location-tag`}
                containerStyle={formFieldInStack}
              />
            </CoolingSystemPermissionField>
          </FormColumn>
        </FormRow>

        <FormRow>
          <FormColumn>
            <CoolingSystemPermissionField
              fieldId="coolingDescription"
              requestStage={requestStage}
              requestId={requestId}
            >
              <FormInput
                label="Description"
                value={formData.coolingDescription || ''}
                onChangeText={value => updateField('coolingDescription', value)}
                placeholder={
                  COOLING_SYSTEM_PLACEHOLDERS.description || undefined
                }
                multiline
                maxLength={10000}
                richText
                requestId={requestId}
                requestSection={detailsSectionId}
                isOnline={!isOffline}
                disabled={disabled}
                containerStyle={formFieldInStack}
              />
            </CoolingSystemPermissionField>
          </FormColumn>
          <FormColumn>
            <CoolingSystemPermissionField
              fieldId="coolingIsExistingSystemOnsite"
              requestStage={requestStage}
              requestId={requestId}
            >
              <FormYesNoChoice
                label="Is there an existing cooling system onsite?"
                value={formData.coolingIsExistingSystemOnsite || ''}
                onSelect={v => updateField('coolingIsExistingSystemOnsite', v)}
                disabled={disabled}
                inline
                testID={`${testID}-existing-system-onsite`}
                containerStyle={formFieldInStack}
              />
            </CoolingSystemPermissionField>
          </FormColumn>
        </FormRow>

        <FormRow>
          <FormColumn>
            <CoolingSystemPermissionField
              fieldId="coolingIsChilledWaterAvailable"
              requestStage={requestStage}
              requestId={requestId}
            >
              <FormYesNoChoice
                label="Is chilled water available onsite?"
                value={String(formData.coolingIsChilledWaterAvailable ?? '')}
                onSelect={value =>
                  updateField('coolingIsChilledWaterAvailable', value)
                }
                disabled={disabled}
                inline
                testID={`${testID}-chilled-water-available`}
                containerStyle={formFieldInStack}
              />
            </CoolingSystemPermissionField>
          </FormColumn>
          <FormColumn>{null}</FormColumn>
        </FormRow>

        <FormRow>
          <FormColumn>
            <CoolingSystemPermissionField
              fieldId="coolingTotalAvailableCapacityKw"
              requestStage={requestStage}
              requestId={requestId}
            >
              <FormInput
                label="Total available cooling capacity (kW)"
                value={formData.coolingTotalAvailableCapacityKw || ''}
                onChangeText={value =>
                  updateField('coolingTotalAvailableCapacityKw', value)
                }
                placeholder={
                  COOLING_SYSTEM_PLACEHOLDERS.totalAvailableCapacityKw
                }
                disabled={disabled}
                containerStyle={formFieldInStack}
              />
            </CoolingSystemPermissionField>
          </FormColumn>
          <FormColumn>
            <CoolingSystemPermissionField
              fieldId="coolingRequiredRedundancyForSystem"
              requestStage={requestStage}
              requestId={requestId}
            >
              <FormDropdown
                label="Cooling Redundancy"
                value={String(
                  formData.coolingRequiredRedundancyForSystem ?? '',
                )}
                options={[...REDUNDANCY_OPTIONS]}
                onSelect={value =>
                  updateField(
                    'coolingRequiredRedundancyForSystem',
                    Array.isArray(value) ? value[0] ?? '' : value,
                  )
                }
                placeholder="Select N, N+1, or 2N"
                disabled={disabled}
                containerStyle={formFieldInStack}
              />
            </CoolingSystemPermissionField>
          </FormColumn>
        </FormRow>

        <FormRow>
          <FormColumn>
            <CoolingSystemPermissionField
              fieldId="coolingTempRisePrefThroughItEquipment"
              requestStage={requestStage}
              requestId={requestId}
            >
              <UnitNumberField
                fieldKey="coolingTempRisePrefThroughItEquipment"
                unitScopeId={formData.id}
                pair="temperature"
                label="Temp rise pref through IT equipment (Rack ΔT)"
                value={String(
                  formData.coolingTempRisePrefThroughItEquipment ?? '',
                )}
                onChangeText={value =>
                  updateField('coolingTempRisePrefThroughItEquipment', value)
                }
                placeholder={COOLING_SYSTEM_PLACEHOLDERS.tempRise}
                showStepper={false}
                disabled={disabled}
                readOnlyMode={disabled}
                containerStyle={formFieldInStack}
              />
            </CoolingSystemPermissionField>
          </FormColumn>
          <FormColumn>
            <CoolingSystemPermissionField
              fieldId="coolingRearDoorHexDesired"
              requestStage={requestStage}
              requestId={requestId}
            >
              <FormYesNoChoice
                label="Rear door heat exchanger desired?"
                value={booleanToYesNo(formData.coolingRearDoorHexDesired)}
                onSelect={v =>
                  updateField('coolingRearDoorHexDesired', v === 'Yes')
                }
                disabled={disabled}
                inline
                testID={`${testID}-rear-door-hex`}
                containerStyle={formFieldInStack}
              />
            </CoolingSystemPermissionField>
          </FormColumn>
        </FormRow>

        <FormRow>
          <FormColumn>
            <CoolingSystemPermissionField
              fieldId="coolingDescribeApproach"
              requestStage={requestStage}
              requestId={requestId}
            >
              <FormInput
                label="Describe Cooling Approach"
                value={formData.coolingDescribeApproach || ''}
                onChangeText={value =>
                  updateField('coolingDescribeApproach', value)
                }
                placeholder={COOLING_SYSTEM_PLACEHOLDERS.describeApproach}
                multiline
                maxLength={10000}
                richText
                requestId={requestId}
                requestSection={detailsSectionId}
                isOnline={!isOffline}
                disabled={disabled}
                containerStyle={formFieldInStack}
              />
            </CoolingSystemPermissionField>
          </FormColumn>
          <FormColumn>
            <CoolingSystemPermissionField
              fieldId="coolingDescribeRearDoorLoop"
              requestStage={requestStage}
              requestId={requestId}
            >
              <FormInput
                label="Describe rear door liquid loop"
                value={formData.coolingDescribeRearDoorLoop || ''}
                onChangeText={value =>
                  updateField('coolingDescribeRearDoorLoop', value)
                }
                placeholder={COOLING_SYSTEM_PLACEHOLDERS.describeRearDoorLoop}
                multiline
                maxLength={10000}
                richText
                requestId={requestId}
                requestSection={detailsSectionId}
                isOnline={!isOffline}
                disabled={disabled}
                containerStyle={formFieldInStack}
              />
            </CoolingSystemPermissionField>
          </FormColumn>
        </FormRow>
      </StepForm>

      {showAdditionalInformation ? (
        <RepeatableInstanceAdditionalInfo
          titleSubtitle="Cooling notes, attachments, or clarifications for the configuration team."
          sectionId={additionalSectionId}
          fieldId="coolingAdditionalReadinessInfo"
          inputLabel="Additional Cooling Readiness Information"
          placeholder="Enter additional cooling readiness information"
          value={String(formData.coolingAdditionalReadinessInfo ?? '')}
          onChangeText={value =>
            updateField('coolingAdditionalReadinessInfo', value)
          }
          requestId={requestId}
          requestStage={permissionStage}
          disabled={disabled}
          subsectionFields={COOLING_NOTES_SUBSECTION_FIELDS}
          headerValues={formData as Record<string, unknown>}
          attachmentsBySection={attachmentsBySection ?? {}}
          onAttachFiles={onAttachFiles}
          onDeleteAttachment={onDeleteAttachment}
          selectedFiles={selectedFiles ?? {}}
          onFilesSelected={onFilesSelected}
          offlineMode={Boolean(isOffline)}
          onCaptionChange={onCaptionChange}
          pendingCaptionEdits={pendingCaptionEdits}
          markDirty={markSharedDirty}
          showFullDetails={showFullDetails}
        />
      ) : null}
    </FormStackColumnsOnWebProvider>
  );
};

const styles = StyleSheet.create({
  sectionCard: {
    marginBottom: spacing.md,
  },
});
